import { useEffect } from 'react'
import Sheet from '@mui/joy/Sheet'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import LinearProgress from '@mui/joy/LinearProgress'
import IconButton from '@mui/joy/IconButton'
import Button from '@mui/joy/Button'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { CrmProcessJob, CrmProcessJobSourceProgress } from 'api/oracleApi'

const STEP_COLORS = {
    fetching: 'neutral',
    extracting: 'primary',
    matching: 'primary',
    committing: 'warning',
    done: 'success',
    skipped: 'neutral',
    failed: 'danger',
} as const

const STEP_LABELS: Record<CrmProcessJobSourceProgress['status'], string> = {
    fetching: 'Fetching',
    extracting: 'Extracting',
    matching: 'Matching',
    committing: 'Committing',
    done: 'Done',
    skipped: 'Up to date',
    failed: 'Failed',
}

const STEP_ORDER: CrmProcessJobSourceProgress['status'][] = ['fetching', 'extracting', 'matching', 'committing', 'done', 'skipped', 'failed']

function stepProgress(s: CrmProcessJobSourceProgress): number {
    const idx = STEP_ORDER.indexOf(s.status)
    if (s.status === 'done' || s.status === 'skipped' || s.status === 'failed') return 100
    // Parse "47/200" or "47/200 (23%)" patterns from the detail string for fine-grained progress
    const match = s.detail?.match(/(\d+)\s*\/\s*(\d+)/)
    if (match) {
        const current = parseInt(match[1], 10)
        const total = parseInt(match[2], 10)
        if (total > 0) {
            const phasePct = current / total
            // Each phase represents 25% of the bar; offset by completed phases
            const phaseBase = (idx / 4) * 100
            const phaseSpan = 100 / 4
            return Math.min(100, Math.round(phaseBase + phasePct * phaseSpan))
        }
    }
    return Math.round(((idx + 1) / 4) * 100)
}

function isDeterminate(s: CrmProcessJobSourceProgress): boolean {
    if (s.status === 'done' || s.status === 'skipped' || s.status === 'failed') return true
    return /\d+\s*\/\s*\d+/.test(s.detail ?? '')
}

function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const secs = Math.floor(diff / 1000)
    if (secs < 60) return `${secs}s ago`
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `${mins}m ago`
    return `${Math.floor(mins / 60)}h ago`
}

function fmtTokens(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return String(n)
}

function SourceRow({ s }: { s: CrmProcessJobSourceProgress }) {
    const active = s.status !== 'done' && s.status !== 'skipped' && s.status !== 'failed'
    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                    <Typography level="body-xs" fontWeight="md" sx={{ textTransform: 'capitalize', minWidth: 52 }}>
                        {s.sourceType}
                    </Typography>
                    <Chip size="sm" color={STEP_COLORS[s.status]} variant="soft" sx={{ fontSize: 10, height: 18, '--Chip-paddingInline': '6px' }}>
                        {STEP_LABELS[s.status]}
                    </Chip>
                    {s.detail && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            {s.detail}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" gap={1} alignItems="center">
                    {s.processedCount > 0 && (
                        <Typography level="body-xs" color="success">
                            {s.processedCount} processed
                        </Typography>
                    )}
                    {s.unattributedCount > 0 && (
                        <Typography level="body-xs" color="warning">
                            {s.unattributedCount} unattributed
                        </Typography>
                    )}
                    {s.failedCount > 0 && (
                        <Typography level="body-xs" color="danger">
                            {s.failedCount} failed
                        </Typography>
                    )}
                    {s.tokenUsage && (s.tokenUsage.inputTokens > 0 || s.tokenUsage.outputTokens > 0) && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontFamily: 'monospace', fontSize: 10 }}>
                            {fmtTokens(s.tokenUsage.inputTokens)}↑ {fmtTokens(s.tokenUsage.outputTokens)}↓
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <LinearProgress
                determinate={!active || isDeterminate(s)}
                value={stepProgress(s)}
                color={STEP_COLORS[s.status] === 'neutral' ? 'neutral' : STEP_COLORS[s.status]}
                size="sm"
                sx={{ height: 3, borderRadius: 2 }}
            />
        </Box>
    )
}

interface Props {
    job: CrmProcessJob
    isCancelling?: boolean
    onCancel?: () => void
    onDismiss: () => void
}

export function ProcessJobCard({ job, isCancelling, onCancel, onDismiss }: Props) {
    useEffect(() => {
        if (job.status === 'done' || job.status === 'failed' || job.status === 'locked' || job.status === 'cancelled') {
            const t = setTimeout(onDismiss, 8000)
            return () => clearTimeout(t)
        }
    }, [job.status, onDismiss])

    const isRunning = job.status === 'running'

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: 'sm',
                p: 1.5,
                borderColor:
                    job.status === 'failed'
                        ? 'danger.outlinedBorder'
                        : job.status === 'done'
                          ? 'success.outlinedBorder'
                          : job.status === 'cancelled'
                            ? 'neutral.outlinedBorder'
                            : 'primary.outlinedBorder',
                bgcolor:
                    job.status === 'failed'
                        ? 'danger.softBg'
                        : job.status === 'done'
                          ? 'success.softBg'
                          : job.status === 'cancelled'
                            ? 'neutral.softBg'
                            : 'background.surface',
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Stack sx={{ flex: 1, gap: 1.25 }}>
                    {/* Header row */}
                    <Stack direction="row" alignItems="center" gap={1}>
                        {isRunning && (
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.500',
                                    animation: 'pulse 1.4s ease-in-out infinite',
                                    '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                                }}
                            />
                        )}
                        {job.status === 'done' && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.500' }} />}
                        {job.status === 'failed' && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'danger.500' }} />}
                        {job.status === 'locked' && <LockOutlinedIcon sx={{ fontSize: 16, color: 'warning.500' }} />}
                        {job.status === 'cancelled' && <CancelOutlinedIcon sx={{ fontSize: 16, color: 'neutral.500' }} />}

                        <Typography level="body-sm" fontWeight="md">
                            {isRunning
                                ? isCancelling
                                    ? 'Cancelling sync...'
                                    : 'Processing knowledge base'
                                : job.status === 'done'
                                  ? 'Processing complete'
                                  : job.status === 'locked'
                                    ? 'Already running'
                                    : job.status === 'cancelled'
                                      ? 'Sync cancelled'
                                      : 'Processing failed'}
                        </Typography>

                        <Typography level="body-xs" color="neutral">
                            {relativeTime(job.started_at)}
                        </Typography>

                        {job.status === 'locked' && (
                            <Typography level="body-xs" color="neutral">
                                · Another sync is in progress
                            </Typography>
                        )}
                        {job.error && (
                            <Typography level="body-xs" color="danger">
                                {job.error}
                            </Typography>
                        )}
                    </Stack>

                    {/* Source rows */}
                    {job.sources.length === 0 && isRunning && (
                        <Stack gap={0.5}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                                    Initializing...
                                </Typography>
                            </Stack>
                            <LinearProgress size="sm" sx={{ height: 3, borderRadius: 2 }} />
                        </Stack>
                    )}

                    {job.sources.length > 0 && (
                        <Stack gap={1}>
                            {job.sources.map((s) => (
                                <SourceRow key={s.sourceType} s={s} />
                            ))}
                            {(() => {
                                const totalIn = job.sources.reduce((s, src) => s + (src.tokenUsage?.inputTokens ?? 0), 0)
                                const totalOut = job.sources.reduce((s, src) => s + (src.tokenUsage?.outputTokens ?? 0), 0)
                                if (totalIn === 0 && totalOut === 0) return null
                                return (
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontFamily: 'monospace', fontSize: 10, textAlign: 'right' }}>
                                        Total tokens: {fmtTokens(totalIn)}↑ in · {fmtTokens(totalOut)}↓ out
                                    </Typography>
                                )
                            })()}
                        </Stack>
                    )}
                </Stack>

                {isRunning && onCancel ? (
                    <Button
                        size="sm"
                        variant="soft"
                        color="danger"
                        startDecorator={<StopCircleOutlinedIcon sx={{ fontSize: 16 }} />}
                        loading={isCancelling}
                        onClick={onCancel}
                        sx={{ flexShrink: 0, mt: -0.25 }}
                    >
                        Cancel
                    </Button>
                ) : (
                    <IconButton size="sm" variant="plain" onClick={onDismiss} sx={{ flexShrink: 0, mt: -0.25 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Stack>
        </Sheet>
    )
}
